import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import type {
  CaseFolder,
  InterrogatoryQueryFolderInfo,
} from "@/briefpoint-client";
import { FolderSearchStatus, ToggleableFeature } from "@/briefpoint-client";
import Button from "@/components/Button";
import Loading from "@/components/Loading";
import { toast } from "@/components/ui/toast";
import { useCaseFileApi, useDocumentApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import folderIconEmpty from "@/images/folder-empty-icon.svg";
import folderIconExclamation from "@/images/folder-exclamation.svg";
import folderIconPartial from "@/images/folder-half-icon.svg";
import folderIcon from "@/images/folder-icon.svg";
import {
  FILE_EXPLORER_VARIANTS,
  RESPONSIVE_FILE_ITEM_CHECKBOX,
  SEARCH_PARAMS,
} from "@/lib/constants";
import {
  getLatestQueryForInterrogatoryOptions,
  getResponsiveFilesForInterrogatoryOptions,
  searchCaseFilesQueryOptions,
} from "@/lib/query-options";
import type { FileExplorerVariant } from "@/lib/types";
import {
  folderDirectory,
  getCaseId,
  getHistoryPath,
  getSearchParamsVariables,
  normalizeError,
  userCanUseFeature,
} from "@/lib/utils";
import CaseFileItem from "./CaseFileItem";
import styles from "./FileExplorerColumn.module.scss";
import ResponsiveFileItem from "./ResponsiveFileItem";

interface Props {
  folderId: string | undefined;
  position: number;
  setSelectedFile: (file: string | null) => void;
  selectedFile: string | null;
  searchColumn?: boolean;
  variant: FileExplorerVariant;
  updateBreadcrumbs: (folder: CaseFolder | null, position: number) => void;
  initializeBreadcrumbs: (folder: CaseFolder[], position: number) => void;
}

export default function FileExplorerColumn(props: Props) {
  const caseFileApi = useCaseFileApi();
  const documentApi = useDocumentApi();
  const queryClient = useQueryClient();
  const columnContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const history = useHistory();
  const { user } = useAuth();
  const userCanUseDocHandling = user
    ? userCanUseFeature(user, ToggleableFeature.Autodoc)
    : false;
  const urlParams = useParams<{
    id: string;
    caseId: string;
    documentId: string;
    currentStep: string;
    firstFolderId: string;
    secondFolderId: string;
    thirdFolderId: string;
  }>();

  const caseId = getCaseId(props.variant, urlParams);

  const historyPath = getHistoryPath(
    props.variant,
    urlParams.documentId,
    urlParams.currentStep,
    caseId,
  );

  const searchParams = new URLSearchParams(location.search);
  const number = searchParams.get("number");
  const search = searchParams.get(SEARCH_PARAMS.SEARCH);
  const interrogatoryIdParam = Number(
    searchParams.get(SEARCH_PARAMS.INTERROGATORY_ID),
  );
  // Set by "Only show folders we didn't look at" button
  const filter = searchParams.get(SEARCH_PARAMS.FILTER);
  const isSearching = props.searchColumn && search !== "";
  const hasFilter = filter === "true";

  const searchParamsVariables = getSearchParamsVariables(
    props.variant,
    interrogatoryIdParam,
    filter,
    search,
    number,
  );

  // Used in wizard version to show the correct folder icon
  // and to to filter folders that have not been searched
  const documentInterrogatoryQuery = useQuery({
    ...getLatestQueryForInterrogatoryOptions(
      urlParams.documentId,
      interrogatoryIdParam,
      documentApi,
    ),
    queryFn: async () => {
      if (!urlParams.documentId) throw new Error("Document ID is required");
      return await documentApi.documentGetLatestQueryForInterrogatory({
        documentId: urlParams.documentId,
        interrogatoryId: interrogatoryIdParam,
      });
    },
    staleTime: 60000,
    enabled:
      props.variant === FILE_EXPLORER_VARIANTS.WIZARD && userCanUseDocHandling,
    select: (data) => data.data,
  });

  const filesQueryParams = {
    caseId,
    // If we are searching, we don't want to pass a folderId
    folderId: isSearching ? undefined : props.folderId,
    search: isSearching && search ? search : undefined,
  };

  const searchCaseFilesQuery = useQuery({
    ...searchCaseFilesQueryOptions(caseFileApi, filesQueryParams),
    staleTime: 30000,
    select: (data) => {
      return {
        files: data?.files
          .filter((f) => !f.isWithheld && !f.isPrivileged)
          .sort((a, b) => a.name.localeCompare(b.name)),
        // Sort folders by name
        folders: filterFolders(data?.folders)?.sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
      };
    },
    retry: false,
  });

  /* used in wizard version to show the correct folder icon */
  function getFolderIcon(
    folders: InterrogatoryQueryFolderInfo[],
    folderId: string,
  ) {
    const matchingFolder = folders?.find(
      (folder) => folder.folderId === folderId,
    );
    if (matchingFolder?.folderSearchStatus === FolderSearchStatus.Full) {
      return folderIcon;
    }
    if (matchingFolder?.folderSearchStatus === FolderSearchStatus.Partial) {
      return folderIconPartial;
    }
    if (
      matchingFolder?.folderSearchStatus ===
      FolderSearchStatus.SomeFilesSelected
    ) {
      return folderIconExclamation;
    }
    return folderIconEmpty;
  }

  function folderIsSelected(id: string) {
    if (props.position === 0) {
      return id === urlParams.firstFolderId;
    }
    if (props.position === 1) {
      return id === urlParams.secondFolderId;
    }
    return id === urlParams.thirdFolderId;
  }

  function enterFolder(folder: CaseFolder) {
    props.updateBreadcrumbs(folder, props.position);
    props.setSelectedFile(null);
    history.push({
      pathname: `${historyPath}/${folderDirectory(folder.id, props.position, urlParams.firstFolderId, urlParams.secondFolderId)}`,
      search: searchParamsVariables,
    });
  }

  function removeErrorFolder() {
    if (props.position === 1) {
      return "";
    }
    if (props.position === 2) {
      return `${urlParams.firstFolderId}`;
    }
    return `${urlParams.firstFolderId}/${urlParams.secondFolderId}`;
  }

  /* biome-ignore lint/correctness/useExhaustiveDependencies: run only when filesQuery data changes */
  useEffect(() => {
    props.setSelectedFile(null);
  }, [searchCaseFilesQuery.data]);

  /* biome-ignore lint/correctness/useExhaustiveDependencies: run only when filesQuery is fetched */
  useEffect(() => {
    if (searchCaseFilesQuery.isFetched) {
      props.initializeBreadcrumbs(
        searchCaseFilesQuery.data?.folders || [],
        props.position,
      );
    }
  }, [searchCaseFilesQuery.isFetched]);

  /* biome-ignore lint/correctness/useExhaustiveDependencies: run only when filesQuery error changes */
  useEffect(() => {
    if (
      searchCaseFilesQuery.error instanceof Response &&
      searchCaseFilesQuery.error.status === 400
    ) {
      // Used for removing the error folder - push used to allow user to navigate in/out of those parameters when set
      history.push({
        pathname: `${historyPath}/${removeErrorFolder()}`,
        search: searchParamsVariables,
      });
    }
  }, [searchCaseFilesQuery.error]);

  function clickVisibleResponsiveCheckboxes(select: boolean) {
    const root = columnContainerRef.current;
    if (!root) {
      return 0;
    }
    const allCheckboxes = root.querySelectorAll(
      `.${RESPONSIVE_FILE_ITEM_CHECKBOX}`,
    );
    let toggled = 0;
    if (select) {
      allCheckboxes.forEach((checkbox: Element) => {
        const checkboxInput = checkbox as HTMLInputElement;
        if (!checkboxInput.checked) {
          checkboxInput.click();
          toggled += 1;
        }
      });
    } else {
      allCheckboxes.forEach((checkbox: Element) => {
        const checkboxInput = checkbox as HTMLInputElement;
        if (checkboxInput.checked) {
          checkboxInput.click();
          toggled += 1;
        }
      });
    }
    return toggled;
  }

  function filterFolders(caseFolders: CaseFolder[] | undefined) {
    if (!hasFilter) {
      return caseFolders;
    }
    /* filter for folders that have not been searched aka no interior folders */
    if (hasFilter) {
      const folderItems = caseFolders?.filter((caseFolder) => {
        const interrogatoryFolder =
          documentInterrogatoryQuery.data?.folders?.find(
            (f) => f.folderId === caseFolder.id,
          );
        if (
          !interrogatoryFolder ||
          interrogatoryFolder?.folderSearchStatus ===
            FolderSearchStatus.NotSearched
        ) {
          return caseFolder;
        }
        return undefined;
      });
      return folderItems;
    }
  }

  // Used in wizard version to decide checked state
  const responsiveFilesQuery = useQuery({
    ...getResponsiveFilesForInterrogatoryOptions(
      urlParams.documentId,
      interrogatoryIdParam,
      documentApi,
      false,
      userCanUseDocHandling,
    ),
    enabled:
      props.variant === FILE_EXPLORER_VARIANTS.WIZARD && userCanUseDocHandling,
  });

  // Calculate if all files are checked based on the data
  const isAllFilesChecked = useMemo(() => {
    if (
      props.variant !== FILE_EXPLORER_VARIANTS.WIZARD ||
      !userCanUseDocHandling ||
      !searchCaseFilesQuery.data?.files?.length
    ) {
      return false;
    }

    const files = searchCaseFilesQuery.data.files;
    const responsiveFiles = responsiveFilesQuery.data || [];

    // Check if all files have a corresponding responsive file that is not withheld
    const allFilesChecked = files.every((file) => {
      const responsiveFile = responsiveFiles.find(
        (rf) => rf.caseFileId === file.id,
      );
      return responsiveFile && !responsiveFile.isWithheld;
    });

    return allFilesChecked && files.length > 0;
  }, [
    props.variant,
    userCanUseDocHandling,
    searchCaseFilesQuery.data?.files,
    responsiveFilesQuery.data,
  ]);

  const folderTreeBulkMutation = useMutation({
    mutationFn: async (variables: {
      mode: "select" | "deselect";
      folderIds: string[];
      skipSuccessToast?: boolean;
    }) => {
      const { mode, folderIds } = variables;
      const results = await Promise.all(
        folderIds.map((folderId) =>
          mode === "select"
            ? documentApi.documentSelectAllFilesInFolderTree({
                documentId: urlParams.documentId,
                interrogatoryId: interrogatoryIdParam,
                folderId,
              })
            : documentApi.documentDeselectAllFilesInFolderTree({
                documentId: urlParams.documentId,
                interrogatoryId: interrogatoryIdParam,
                folderId,
              }),
        ),
      );
      return results.reduce((sum, n) => sum + Number(n), 0);
    },
    onSuccess: (total, variables) => {
      if (!variables.skipSuccessToast) {
        if (variables.mode === "select") {
          toast.success(`${total} files selected`);
        } else {
          toast.success(`${total} files deselected`);
        }
      }
      queryClient.invalidateQueries(
        getResponsiveFilesForInterrogatoryOptions(
          urlParams.documentId,
          interrogatoryIdParam,
          documentApi,
          false,
          userCanUseDocHandling,
        ),
      );
    },
    onError: (error, variables) => {
      toast.error(
        variables.mode === "select"
          ? "Error: Failed to select files"
          : "Error: Failed to deselect files",
      );
      rg4js("send", {
        error: normalizeError(error),
      });
    },
  });

  function selectOrDeselectAllFiles() {
    if (folderTreeBulkMutation.isPending) {
      return;
    }
    const selecting = !isAllFilesChecked;
    if (props.folderId) {
      folderTreeBulkMutation.mutate({
        mode: selecting ? "select" : "deselect",
        folderIds: [props.folderId],
      });
      return;
    }
    const n = clickVisibleResponsiveCheckboxes(selecting);
    if (n > 0) {
      if (selecting) {
        toast.success(`${n} files selected`);
      } else {
        toast.success(`${n} files deselected`);
      }
    }
  }

  /** listed file checkboxes first (avoids folder bulk disabling inputs). then folder-tree bulk for search-hit folders. folder-only: bulk only. */
  async function handleSearchSelectAllClick() {
    if (folderTreeBulkMutation.isPending) {
      return;
    }
    const files = searchCaseFilesQuery.data?.files ?? [];
    const folders = searchCaseFilesQuery.data?.folders ?? [];
    const searchResultFolderIds = folders.map((f) => f.id);
    let didSomething = false;
    try {
      if (files.length > 0) {
        const n = clickVisibleResponsiveCheckboxes(true);
        if (n > 0) {
          didSomething = true;
        }
      }
      if (searchResultFolderIds.length > 0) {
        await folderTreeBulkMutation.mutateAsync({
          mode: "select",
          folderIds: searchResultFolderIds,
          skipSuccessToast: true,
        });
        didSomething = true;
      }
      if (didSomething) {
        toast.success("Files selected");
      }
    } catch {
      // mutation onError already reports failure
    }
  }

  async function handleSearchDeselectAllClick() {
    if (folderTreeBulkMutation.isPending) {
      return;
    }
    const files = searchCaseFilesQuery.data?.files ?? [];
    const folders = searchCaseFilesQuery.data?.folders ?? [];
    const searchResultFolderIds = folders.map((f) => f.id);
    let didSomething = false;
    try {
      if (files.length > 0) {
        const n = clickVisibleResponsiveCheckboxes(false);
        if (n > 0) {
          didSomething = true;
        }
      }
      if (searchResultFolderIds.length > 0) {
        await folderTreeBulkMutation.mutateAsync({
          mode: "deselect",
          folderIds: searchResultFolderIds,
          skipSuccessToast: true,
        });
        didSomething = true;
      }
      if (didSomething) {
        toast.success("Files deselected");
      }
    } catch {
      // mutation onError already reports failure
    }
  }

  /** column 2 lists contents of the first-column folder; bulk applies to that folder tree only */
  function applySecondColumnSubtreeBulk(mode: "select" | "deselect") {
    if (
      folderTreeBulkMutation.isPending ||
      props.position !== 1 ||
      !props.folderId
    ) {
      return;
    }
    folderTreeBulkMutation.mutate({
      mode,
      folderIds: [props.folderId],
    });
  }

  const isWizardAndCanUseDocHandling =
    props.variant === FILE_EXPLORER_VARIANTS.WIZARD && userCanUseDocHandling;

  const columnHasFilesOrFolders =
    (searchCaseFilesQuery.data?.files?.length || 0) > 0 ||
    (searchCaseFilesQuery.data?.folders?.length || 0) > 0;

  const columnHasFiles = (searchCaseFilesQuery.data?.files?.length || 0) > 0;

  const showSearchBulkActions =
    props.searchColumn === true &&
    isSearching &&
    isWizardAndCanUseDocHandling &&
    columnHasFilesOrFolders;

  const showSecondColumnSubtreeBulkActions =
    props.position === 1 &&
    isWizardAndCanUseDocHandling &&
    props.folderId !== undefined &&
    props.folderId !== "" &&
    columnHasFilesOrFolders;

  const showNonSearchSelectAllToggle =
    !isSearching &&
    props.position !== 1 &&
    isWizardAndCanUseDocHandling &&
    columnHasFiles;

  const folderTreeBulkPending = folderTreeBulkMutation.isPending;

  return (
    <div
      ref={columnContainerRef}
      className={styles.container}
      data-testid="file-explorer-column"
    >
      {showSearchBulkActions && (
        <div className={styles.bulkActionBar}>
          <Button
            variant="link"
            size="sm"
            className="p-0 outline-none"
            disabled={folderTreeBulkPending}
            onClick={handleSearchSelectAllClick}
          >
            Select All
          </Button>
          <Button
            variant="link"
            size="sm"
            className="p-0 outline-none"
            disabled={folderTreeBulkPending}
            onClick={handleSearchDeselectAllClick}
          >
            Deselect All
          </Button>
        </div>
      )}
      {showSecondColumnSubtreeBulkActions && (
        <div className={styles.bulkActionBar}>
          <Button
            variant="link"
            size="sm"
            className="p-0 outline-none"
            disabled={folderTreeBulkPending}
            onClick={() => {
              applySecondColumnSubtreeBulk("select");
            }}
          >
            Select All
          </Button>
          <Button
            variant="link"
            size="sm"
            className="p-0 outline-none"
            disabled={folderTreeBulkPending}
            onClick={() => {
              applySecondColumnSubtreeBulk("deselect");
            }}
          >
            Deselect All
          </Button>
        </div>
      )}
      {showNonSearchSelectAllToggle && (
        <Button
          variant="link"
          onClick={selectOrDeselectAllFiles}
          size="sm"
          className="p-0 ps-2 outline-none"
          disabled={folderTreeBulkPending}
        >
          {isAllFilesChecked ? "Deselect All" : "Select All"}
        </Button>
      )}
      <Loading
        align="left"
        className={styles.loadingSpinner}
        isLoading={searchCaseFilesQuery.isLoading}
      >
        <div>
          {searchCaseFilesQuery.data?.files?.length === 0 &&
            searchCaseFilesQuery.data?.folders?.length === 0 && (
              <div data-testid="no-files-found" className={styles.noFiles}>
                {props.searchColumn
                  ? "No results found"
                  : `The contents of this folder have been ${
                      hasFilter ? "hidden" : "deleted"
                    } or designated as privileged.`}
              </div>
            )}
          {searchCaseFilesQuery.data?.files.map((file) =>
            props.variant === FILE_EXPLORER_VARIANTS.WIZARD ? (
              <ResponsiveFileItem
                key={file.id}
                file={file}
                responsiveFile={responsiveFilesQuery.data?.find(
                  (rf) => rf.caseFileId === file.id,
                )}
                selectedFile={props.selectedFile}
                onSelect={(fileId) => props.setSelectedFile(fileId)}
                bulkActionInProgress={folderTreeBulkPending}
              />
            ) : (
              <CaseFileItem
                key={file.id}
                file={file}
                selectedFile={props.selectedFile}
                onSelect={(fileId) => props.setSelectedFile(fileId)}
              />
            ),
          )}
        </div>
        <div>
          {searchCaseFilesQuery.data?.folders?.map((folder) => (
            <div
              className={`${folderIsSelected(folder.id) && styles.selected}`}
              key={folder.id}
            >
              <Button
                key={folder.id}
                title={folder.name}
                variant="link"
                className={`border-0 ps-2 text-decoration-none ${styles.item}`}
                onClick={() => enterFolder(folder)}
              >
                {props.variant === FILE_EXPLORER_VARIANTS.CASE ? (
                  <img src={folderIcon} alt="folder" />
                ) : (
                  <img
                    src={getFolderIcon(
                      documentInterrogatoryQuery.data?.folders || [],
                      folder.id,
                    )}
                    alt="folder"
                  />
                )}
                <div data-testid="folder-name" className={styles.name}>
                  {folder.name}
                </div>
              </Button>
            </div>
          ))}
        </div>
      </Loading>
    </div>
  );
}
